import { useRouter } from '../../lib/router'
import { MarketingLayout } from './MarketingLayout'
import { useIsMobile } from '../../lib/useIsMobile'

type FunnelKind = 'workshop' | 'whatsapp' | 'dvi' | 'payments' | 'accounting'

const FUNNELS: Record<FunnelKind, {
  path: string
  eyebrow: string
  title: string
  description: string
  hero: string
  primaryCta: string
  secondaryCta: string
  pain: string[]
  outcomes: string[]
  proof: string
  faqs: { q: string; a: string }[]
}> = {
  workshop: {
    path: '/workshop-management-software',
    eyebrow: 'Workshop management software South Africa',
    title: 'Workshop management software built around WhatsApp, job cards and getting paid',
    description: 'AutoAxxiz is workshop management software for South African independent auto workshops: job cards, DVI, WhatsApp quotes, invoices, payment links and accounting sync.',
    hero: 'Run intake, inspections, quotes, WhatsApp customer updates, invoices and payments from one system instead of paper job cards, personal phones and spreadsheets.',
    primaryCta: 'Book a workshop demo',
    secondaryCta: 'Compare pricing',
    pain: [
      'Job cards live on paper, WhatsApp and memory.',
      'Quote approvals get buried in personal chats.',
      'Invoices go out late and payments need manual chasing.',
      'The owner is the only person who knows what is actually happening.',
    ],
    outcomes: [
      'Create a job card in minutes and keep every update attached to the vehicle.',
      'Send quotes and invoices over WhatsApp with approval/payment workflows.',
      'Give office staff, foremen and mechanics the right role-based screens.',
      'See open jobs, unpaid invoices and customer history without hunting through chats.',
    ],
    proof: 'Built for 2-10 bay independent workshops in South Africa, with ZAR pricing, 15% VAT handling and POPIA export built in.',
    faqs: [
      { q: 'Is this only for mechanical workshops?', a: 'No. The workflow fits mechanical, auto electrical, tyre, panel and general service workshops. DVI checklist items can be adapted around the work you do.' },
      { q: 'Can my team use it on phones?', a: 'Yes. The web app works on phones/tablets, and the Expo mobile app supports workshop-floor DVI and job workflows.' },
      { q: 'Can I keep using WhatsApp?', a: 'Yes. AutoAxxiz connects directly to WhatsApp Cloud API so customer communication is still WhatsApp, but tied to job cards, quotes and invoices.' },
    ],
  },
  whatsapp: {
    path: '/whatsapp-workshop-management',
    eyebrow: 'WhatsApp workshop management',
    title: 'Turn WhatsApp from a chaotic inbox into a tracked workshop workflow',
    description: 'Shared WhatsApp inbox, quote approval by reply, job status updates and invoice/payment messages for South African auto workshops.',
    hero: 'Customers stay on WhatsApp. Your workshop gets shared inboxes, message history, quote approval records and automated updates connected to each job card.',
    primaryCta: 'See the WhatsApp workflow',
    secondaryCta: 'View pricing',
    pain: [
      'The owner’s personal WhatsApp becomes the workshop CRM.',
      'Customers approve work in chats that nobody can find later.',
      'Office staff keep answering “is my car ready?” all day.',
      'There is no clean audit trail when a quote is disputed.',
    ],
    outcomes: [
      'Use a shared multi-agent inbox for workshop conversations.',
      'Let customers approve or reject quotes from WhatsApp replies.',
      'Send booking, quote, invoice, payment and ready-for-collection messages.',
      'Keep every customer message tied to the right conversation and job.',
    ],
    proof: 'Direct Meta WhatsApp Cloud API integration is already built, including webhook handling, inbound approval parsing and inbox updates.',
    faqs: [
      { q: 'Do customers need a new app?', a: 'No. Customers use normal WhatsApp. AutoAxxiz handles the business-side workflow.' },
      { q: 'Can quote approvals happen by text reply?', a: 'Yes. Replies like “yes”, “ok”, “approve” or quick-reply buttons can approve the latest sent quote and advance the job.' },
      { q: 'Can multiple staff manage the inbox?', a: 'Yes. The shared inbox is designed for office/owner/foreman workflows rather than one personal phone.' },
    ],
  },
  dvi: {
    path: '/digital-vehicle-inspection-software',
    eyebrow: 'Digital vehicle inspection software',
    title: 'Digital vehicle inspections that help customers say yes to the work',
    description: 'AutoAxxiz DVI lets mechanics capture inspection checklist results, photos, videos, AI summaries and PDFs for South African workshops.',
    hero: 'Capture PASS, ADVISORY and FAIL findings with photo/video evidence, build trust with customers, and turn inspection findings into quote-ready work.',
    primaryCta: 'Book a DVI demo',
    secondaryCta: 'See pricing',
    pain: [
      'Customers do not trust “you need brakes” without proof.',
      'Inspection notes disappear before the quote is written.',
      'Mechanics take photos on personal phones with no job-card link.',
      'Load-shedding interrupts workshop-floor capture.',
    ],
    outcomes: [
      'Capture checklist items, notes, photos and videos per inspection point.',
      'Save local DVI drafts and sync pending media when back online.',
      'Generate customer-facing inspection PDFs.',
      'Use AI-assisted inspection summaries with fallback text when AI is unavailable.',
    ],
    proof: 'The mobile app now supports DVI drafts, media retry, camera/gallery capture and sync status, with API upload tickets for local or S3 storage.',
    faqs: [
      { q: 'Does DVI work offline?', a: 'It supports local DVI drafts and pending media retry. Full WatermelonDB-style offline-first sync remains a future hardening item.' },
      { q: 'Can we capture videos?', a: 'Yes. The mobile DVI flow supports photo and video capture/upload for inspection points.' },
      { q: 'Where is media stored?', a: 'Development can use local storage. Production can use S3 pre-signed upload/read URLs with South African region storage.' },
    ],
  },
  payments: {
    path: '/workshop-payment-links',
    eyebrow: 'Payment links for workshops',
    title: 'Send invoices with payment links and stop chasing proof-of-payment screenshots',
    description: 'AutoAxxiz supports PayFast/SnapScan payment links, invoice reminders and webhook-based paid status for workshops.',
    hero: 'Create invoices from approved quotes, send them over WhatsApp, and let payment webhooks mark invoices paid without manual spreadsheet checking.',
    primaryCta: 'See payment workflow',
    secondaryCta: 'View pricing',
    pain: [
      'Fake or delayed proof-of-payment screenshots waste office time.',
      'Invoices are sent without a clear payment action.',
      'Manual reconciliation means month-end is always late.',
      'The team cannot see who still owes money.',
    ],
    outcomes: [
      'Generate PayFast and SnapScan payment links from invoice settings.',
      'Receive PayFast and SnapScan payment webhooks.',
      'Auto-mark invoices paid or partially paid.',
      'Send payment confirmation over WhatsApp and notify mechanics.',
    ],
    proof: 'Payment provider config, invoice payment links, webhook reconciliation and manual payment capture are built in the current MVP.',
    faqs: [
      { q: 'Which payment providers are supported?', a: 'Pilot payment workflows use PayFast and SnapScan links and webhook reconciliation, subject to provider setup and validation. Yoco is outside the current pilot scope.' },
      { q: 'Can we still record EFT or cash?', a: 'Yes. Manual payment capture remains available for EFT, cash and other methods.' },
      { q: 'Are payment links sent over WhatsApp?', a: 'Yes. Invoice WhatsApp templates include the configured invoice payment link when available.' },
    ],
  },
  accounting: {
    path: '/workshop-accounting-sync',
    eyebrow: 'Workshop accounting sync',
    title: 'Sync workshop invoices to Xero, Sage or Zoho without re-keying',
    description: 'AutoAxxiz supports a multi-provider accounting model for Xero, Sage and Zoho invoice sync workflows.',
    hero: 'Create SARS-style invoices in AutoAxxiz, then sync accounting references back to the tenant configuration without forcing every workshop onto one provider.',
    primaryCta: 'Discuss accounting setup',
    secondaryCta: 'View pricing',
    pain: [
      'Bookkeepers re-key workshop invoices into accounting systems.',
      'Different workshops use different accounting providers.',
      'Accounting setup is painful during onboarding.',
      'There is no status view for whether an invoice has synced.',
    ],
    outcomes: [
      'Choose accounting provider per tenant: Xero, Sage, Zoho or none.',
      'Configure provider credentials/status from Settings.',
      'Sync invoices and store accounting references.',
      'Use one model so future providers can be added cleanly.',
    ],
    proof: 'The MVP now includes provider config, Xero OAuth/status/sync, Sage service structure and Zoho OAuth/status/sync paths.',
    faqs: [
      { q: 'Is Xero the only accounting provider?', a: 'No. The provider model supports Xero, Sage, Zoho and none, so workshops are not locked into one accounting platform.' },
      { q: 'Is this fully live with real credentials?', a: 'The code paths are built. Real provider credentials and sandbox/live validation are still part of pilot hardening.' },
      { q: 'Can a workshop operate without accounting sync?', a: 'Yes. The system still creates PDFs, tracks invoices and supports data export without an accounting provider.' },
    ],
  },
}

function CheckList({ items }: { items: string[] }) {
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
      {items.map(item => (
        <li key={item} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', color: 'var(--fg-2)', fontSize: 15, lineHeight: 1.55 }}>
          <span style={{ color: 'var(--success-500)', fontWeight: 700, marginTop: 1 }}>✓</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

export function FunnelPage({ kind }: { kind: FunnelKind }) {
  const page = FUNNELS[kind]
  const { navigate } = useRouter()
  const isMobile = useIsMobile()

  return (
    <MarketingLayout>

      <section style={{ background: 'var(--navy-900)', padding: '88px var(--space-6) 76px' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 0.9fr', gap: isMobile ? 32 : 48, alignItems: 'center' }}>
          <div>
            <p className="eyebrow" style={{ color: 'var(--orange-400)', marginBottom: 14 }}>{page.eyebrow}</p>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(34px, 5vw, 56px)', lineHeight: 1.08, color: '#fff', marginBottom: 22 }}>
              {page.title}
            </h1>
            <p style={{ fontSize: 19, lineHeight: 1.65, color: 'var(--navy-200)', maxWidth: 640, marginBottom: 34 }}>
              {page.hero}
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button onClick={() => navigate(`/contact?intent=${kind}`)} style={{ padding: '14px 24px', borderRadius: 8, border: 0, background: 'var(--accent)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                {page.primaryCta}
              </button>
              <button onClick={() => navigate('/pricing')} style={{ padding: '14px 24px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                {page.secondaryCta}
              </button>
            </div>
          </div>

          <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid rgba(255,255,255,0.16)' }}>
            <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ height: 42, background: 'var(--navy-700)', display: 'flex', alignItems: 'center', gap: 7, padding: '0 14px' }}>
                <span style={{ width: 8, height: 8, borderRadius: 4, background: '#ef4444' }} />
                <span style={{ width: 8, height: 8, borderRadius: 4, background: '#f59e0b' }} />
                <span style={{ width: 8, height: 8, borderRadius: 4, background: '#22c55e' }} />
              </div>
              <div style={{ padding: 18, display: 'grid', gap: 12 }}>
                {page.outcomes.slice(0, 4).map((outcome, index) => (
                  <div key={outcome} style={{ display: 'grid', gridTemplateColumns: '34px 1fr', gap: 12, alignItems: 'center' }}>
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: index === 0 ? 'var(--orange-500)' : 'var(--navy-50)' }} />
                    <div>
                      <div style={{ height: 10, width: `${86 - index * 8}%`, background: 'var(--navy-100)', borderRadius: 8, marginBottom: 8 }} />
                      <div style={{ height: 8, width: `${62 - index * 5}%`, background: 'var(--border-subtle)', borderRadius: 8 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p style={{ fontSize: 13, color: 'var(--fg-3)', margin: '14px 0 0', lineHeight: 1.6 }}>
              {page.proof}
            </p>
          </div>
        </div>
      </section>

      <section style={{ padding: '74px var(--space-6)', background: '#fff' }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 40 : 56 }}>
          <div>
            <p className="eyebrow" style={{ marginBottom: 12 }}>What it fixes</p>
            <h2 style={{ fontSize: 34, color: 'var(--navy-900)', marginBottom: 22 }}>The old workflow is costing more than software.</h2>
            <CheckList items={page.pain} />
          </div>
          <div>
            <p className="eyebrow" style={{ marginBottom: 12 }}>What changes</p>
            <h2 style={{ fontSize: 34, color: 'var(--navy-900)', marginBottom: 22 }}>One connected flow your team can actually use.</h2>
            <CheckList items={page.outcomes} />
          </div>
        </div>
      </section>

      <section style={{ padding: '72px var(--space-6)', background: 'var(--navy-50)' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <h2 style={{ fontSize: 32, color: 'var(--navy-900)', textAlign: 'center', marginBottom: 32 }}>Questions buyers ask before booking</h2>
          <div style={{ display: 'grid', gap: 12 }}>
            {page.faqs.map(faq => (
              <div key={faq.q} style={{ background: '#fff', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '20px 22px' }}>
                <h3 style={{ fontSize: 16, color: 'var(--navy-800)', marginBottom: 8 }}>{faq.q}</h3>
                <p style={{ fontSize: 14, color: 'var(--fg-3)', lineHeight: 1.7, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '72px var(--space-6)', background: 'var(--navy-900)', textAlign: 'center' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <h2 style={{ fontSize: 34, color: '#fff', marginBottom: 14 }}>Ready to see it on your own workflow?</h2>
          <p style={{ color: 'var(--navy-200)', fontSize: 17, lineHeight: 1.6, marginBottom: 28 }}>
            Book a focused walkthrough. We’ll map AutoAxxiz against your intake, quote, payment and handover process.
          </p>
          <button onClick={() => navigate(`/contact?intent=${kind}`)} style={{ padding: '14px 28px', borderRadius: 8, border: 0, background: 'var(--accent)', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
            Book the walkthrough
          </button>
        </div>
      </section>
    </MarketingLayout>
  )
}
